import PreviewIcon from "@mui/icons-material/Preview";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Dropzone } from "components/Dropzone";
import pluralize from "pluralize";

export type DocumentFilePickerProps = {
  files: File[];
  onDrop: (files: File[]) => void;
  onRemove: (index: number) => void;
  onPreview?: (file: File) => void;
  canAddMore: boolean;
  maxFiles?: number;
  multiple?: boolean;
  disabled?: boolean;
  accept?: string | string[];
  helperText?: string;
  showFileList?: boolean;
  showPreviewButton?: boolean;
  dropzoneHeight?: string | number;
  dropzoneMinWidth?: string | number;
};

const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  }
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
};

export function DocumentFilePicker({
  files,
  onDrop,
  onRemove,
  onPreview,
  canAddMore,
  maxFiles = Infinity,
  multiple = false,
  disabled = false,
  accept,
  helperText,
  showFileList = true,
  showPreviewButton = true,
  dropzoneHeight,
  dropzoneMinWidth,
}: DocumentFilePickerProps) {
  const theme = useTheme();

  const dropzoneStyles: Record<string, string | number | undefined> = {
    height: dropzoneHeight ?? theme.spacing(8),
  };
  if (dropzoneMinWidth !== undefined) {
    dropzoneStyles.minWidth = dropzoneMinWidth;
  }

  const handleDrop = (droppedFiles: File[]) => {
    if (!canAddMore) {
      return;
    }
    onDrop(droppedFiles);
  };

  const dropzoneLabel = multiple
    ? "Drag and drop your documents or click to select files"
    : "Drag and drop your document or click to select a file";

  return (
    <Stack spacing={2}>
      {helperText && <Typography color="text.secondary">{helperText}</Typography>}
      {showFileList && files.length > 0 && (
        <Stack spacing={1}>
          <List dense>
            {files.map((file, index) => (
              <Stack
                key={`${file.name}-${index}`}
                direction="row"
                spacing={1}
                alignItems="center"
                borderRadius="4px"
                sx={{ "&:not(:last-child)": { mb: theme.spacing(2) } }}
              >
                <ListItem
                  sx={{ bgcolor: "#517BC61F", borderRadius: "4px" }}
                  secondaryAction={
                    showPreviewButton && onPreview ? (
                      <IconButton
                        edge="end"
                        aria-label="preview-document"
                        onClick={() => onPreview(file)}
                        color="primary"
                      >
                        <PreviewIcon />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemText primary={file.name} secondary={formatFileSize(file.size)} />
                </ListItem>
                <IconButton
                  size="medium"
                  aria-label="remove-document"
                  onClick={() => onRemove(index)}
                >
                  <RemoveIcon />
                </IconButton>
              </Stack>
            ))}
          </List>
        </Stack>
      )}
      {canAddMore && (
        <Dropzone
          label={dropzoneLabel}
          dragActiveText={multiple ? "Drop files here" : "Drop file here"}
          onDrop={handleDrop}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          sx={dropzoneStyles}
        />
      )}

      {files.length > 0 && multiple && (
        <Typography variant="caption" color="text.secondary">
          {files.length} {pluralize("file", files.length)} selected
          {Number.isFinite(maxFiles) ? ` (max ${maxFiles})` : ""}
        </Typography>
      )}
    </Stack>
  );
}
